import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card'
import { cn } from '../../utils/cn'
import { checkPasswordStrength } from '../../utils/validation'
import api from '../../services/api'

const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password is too long')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/[a-z]/, 'Must contain a lowercase letter')
      .regex(/[0-9]/, 'Must contain a number')
      .regex(/[^A-Za-z0-9]/, 'Must contain a special character'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  })

type ResetPasswordData = z.infer<typeof resetPasswordSchema>

interface ResetPasswordFormProps {
  token: string
  className?: string
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token, className }) => {
  const [success, setSuccess] = useState(false)
  const [globalError, setGlobalError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onBlur',
    defaultValues: { new_password: '', confirm_password: '' },
  })

  const passwordValue = watch('new_password', '')
  const strength = checkPasswordStrength(passwordValue)

  const onSubmit = async (data: ResetPasswordData) => {
    setGlobalError('')

    if (!token) {
      setGlobalError('Missing reset token. Please use the link from your email.')
      return
    }

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: data.new_password,
      })
      setSuccess(true)
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Something went wrong'
      if (detail.toLowerCase().includes('invalid') || detail.toLowerCase().includes('expired')) {
        setGlobalError('This reset link is invalid or has expired. Please request a new one.')
      } else if (detail.toLowerCase().includes('rate limit') || detail.toLowerCase().includes('too many')) {
        setGlobalError('Too many attempts. Please wait a few minutes.')
      } else {
        setGlobalError(detail)
      }
    }
  }

  if (success) {
    return (
      <Card className={cn('w-full max-w-md mx-auto', className)}>
        <CardContent className="pt-8 text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h2 className="text-xl font-semibold text-foreground">Password reset</h2>
          <p className="text-muted-foreground">
            Your password has been updated successfully. You can now sign in.
          </p>
          <Link to="/auth/login">
            <Button variant="primary" size="lg" className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-500 text-white">
              Go to Login
            </Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  if (!token) {
    return (
      <Card className={cn('w-full max-w-md mx-auto', className)}>
        <CardContent className="pt-8 text-center space-y-4">
          <p className="text-muted-foreground">
            Invalid reset link. Please request a new password reset.
          </p>
          <Link to="/auth/forgot-password">
            <Button variant="primary" size="lg" className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-500 text-white">
              Request New Link
            </Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  const strengthColor =
    strength.score <= 2 ? 'bg-destructive' : strength.score <= 3 ? 'bg-yellow-500' : 'bg-emerald-500'

  return (
    <Card className={cn('w-full max-w-md mx-auto', className)}>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-foreground">
          Set new password
        </CardTitle>
        <CardDescription>
          Choose a strong password for your account.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {globalError && (
            <div
              className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg"
              role="alert"
            >
              {globalError}
            </div>
          )}

          {/* New password */}
          <div className="space-y-2">
            <label htmlFor="new_password" className="block text-sm font-medium text-foreground">
              New password
            </label>
            <Input
              id="new_password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              disabled={isSubmitting}
              error={errors.new_password?.message}
              startIcon={<Lock className="h-4 w-4" />}
              endIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              placeholder="Enter new password"
              {...register('new_password')}
            />

            {/* Strength meter */}
            {passwordValue.length > 0 && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        'h-1 flex-1 rounded-full transition-colors',
                        i < strength.score ? strengthColor : 'bg-muted'
                      )}
                    />
                  ))}
                </div>
                {strength.feedback.length > 0 && (
                  <p className="text-xs text-muted-foreground">{strength.feedback[0]}</p>
                )}
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div className="space-y-2">
            <label htmlFor="confirm_password" className="block text-sm font-medium text-foreground">
              Confirm password
            </label>
            <Input
              id="confirm_password"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              disabled={isSubmitting}
              error={errors.confirm_password?.message}
              startIcon={<Lock className="h-4 w-4" />}
              endIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirm((p) => !p)}
                  className="text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              placeholder="Confirm new password"
              {...register('confirm_password')}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-500 text-white"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting...' : 'Reset password'}
          </Button>

          <div className="text-center">
            <Link to="/auth/login" className="text-sm text-primary hover:text-primary/80">
              Back to login
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ResetPasswordForm
